import React, { useContext, useState, useEffect, useRef } from "react";
import { Typography, useMediaQuery } from "@mui/material";
import EditIcon from "../../assets/icons-svg/edit-icon.svg";
import { Formik } from "formik";
import { VIEWS, ROUTES, ONBOARDING_STEPS } from "../constants/user-info.const";
import { AppContext } from "../routing/Routing";
import PersonalInfoEditModal from "./review-edit/PersonalInfoEditModal";
import AddressInfoEditModal from "./review-edit/AddressInfoEditModal";
import FederalTaxInfoEditModal from "./review-edit/FederalTaxInfoEditModal";
import { useNavigate } from "react-router-dom";
import DownwardArrow from "../../assets/icons-svg/downward-arrow.svg";
import {
  finishOnBoarding,
  getAllDetails,
} from "../../services/api-services/reviewInfoService";
import Loader from "../common/Loader";
import {
  CompleteEmployeeDetails,
  PersonalInfo,
  HomeAddress,
  FederalTaxInfo,
  PaymentMethod,
  BankAccount,
  PAYMENT_TYPES,
} from "./models/user-interface";
import store from "../../store";
import { showAlert } from "../../store/actions/feedback";
import { onboardingActions } from "../../store/actions/steps";
import StateTaxInfoEditModal from "./review-edit/StateTaxInfoEditModal";
import { connect } from "react-redux";
import PaymentMethodEditModal from "./review-edit/PaymentMethodEditModal";
import debounce from "../../utils/debounce";
import hideScroll from "../../utils/hideScroll";

function ReviewInfo(props: any) {
  const { usStates, isNavigateToNext } = props;
  const { viewName, setViewName } = useContext(AppContext);
  const smDevice = useMediaQuery("(max-width:600px)");
  const [showPersonalInfoEdit, setShowPersonalInfoEdit] = useState(false);
  const [showAddressInfoEdit, setShowAddressInfoEdit] = useState(false);
  const [showFederalTaxEdit, setShowFederalTaxEdit] = useState(false);
  const [showStateTaxEdit, setShowStateTaxEdit] = useState(false);
  const [showPaymentMethodEdit, setShowPaymentMethodEdit] = useState(false);
  const [employeeData, setEmployeeData] = useState<CompleteEmployeeDetails>();
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>();
  const [homeAddress, setHomeAddress] = useState<HomeAddress>();
  const [federalTaxInfo, setFederalTaxInfo] = useState<FederalTaxInfo>();
  const [paymentDetails, setPaymentDetails] = useState<PaymentMethod>();
  const [stateTaxes, setStateTaxes] = useState<any>({});
  const [stateTaxesUnformatted, setStateTaxesUnformatted] = useState<any>({});
  const [bankAccount, setBankAccount] = useState<BankAccount>();
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollable, setIsScrollable] = useState<boolean>(true);
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const [isShowFooterLoader, setIsShowFooterLoader] = useState(false);

  const hideScrollButton = debounce(
    () =>
      hideScroll(
        () => setIsScrollable(false),
        () => setIsScrollable(true)
      ),
    50
  );
  useEffect(() => {
    window.addEventListener("scroll", hideScrollButton);
    return () => {
      window.removeEventListener("scroll", hideScrollButton);
    };
  }, []);

  const formatDataForPost = () => {
    const employee: any = {};
    employee["personal_info"] = personalInfo;
    employee["personal_info"].home_address = homeAddress;
    employee["federal_tax_info"] = federalTaxInfo;
    employee["state_taxes"] = stateTaxesUnformatted;
    employee["payment_method"] = paymentDetails;
    if (bankAccount) {
      employee["bank_accounts"] = bankAccount;
    }
    return employee;
  };

  const navigateToTermsOfService = () => {
    if (isNavigateToNext === false) return;
    setViewName(VIEWS.termsOfService);
    navigate(ROUTES.termsOfService);
  };
  const updateCompletedStep = () => {
    store.dispatch({
      type: onboardingActions.UPDATE_ONBOARDING_STEPS,
      state: ONBOARDING_STEPS.reviewInfo,
    });
  };
  const onSubmit = () => {
    const employee = formatDataForPost();
    setIsShowFooterLoader(true);
    finishOnBoarding(employee)
      .then(() => {
        setIsShowFooterLoader(false);
        navigateToTermsOfService();
        updateCompletedStep();
      })
      .catch((error) => {
        setIsShowFooterLoader(false);
        showErrorMessage(error);
      });
  };
  const getOptionLabelfromValue = (options, value): string => {
    let label = "";
    options.forEach((option) => {
      if (option?.value === value) {
        label = option?.label;
      }
    });
    return label;
  };
  const formatStateTaxes = (data) => {
    const stateTaxFormattedResponse = {};
    stateTaxFormattedResponse["state"] = data?.state;
    const questions = data?.questions;
    if (!questions) return;
    questions.forEach((question) => {
      let answer = "";
      const fieldName = question.label;
      const value = question?.answers[0]?.value;
      if (question?.input_question_format?.type === "Select") {
        answer = getOptionLabelfromValue(
          question.input_question_format?.options,
          value
        );
      } else {
        answer = value;
      }

      stateTaxFormattedResponse[fieldName] = answer;
    });
    setStateTaxes(stateTaxFormattedResponse);
  };
  const showErrorMessage = (error) => {
    const errorMessage = error.status?.message;
    store.dispatch(
      showAlert({
        alertType: "error",
        alertText: errorMessage,
      })
    );
  };
  const setValuesForEdit = (data) => {
    setPersonalInfo(data?.personal_info);
    setHomeAddress(data?.personal_info?.home_address);
    setFederalTaxInfo(data?.federal_tax_info);
    setStateTaxesUnformatted(data?.state_taxes);
    formatStateTaxes(data?.state_taxes);
    setPaymentDetails(data?.payment_method);
    data?.bank_accounts
      ? setBankAccount(data?.bank_accounts[0])
      : setBankAccount(undefined);
  };
  const fetchReviewInfo = () => {
    setIsLoading(true);
    getAllDetails()
      .then((res: any) => {
        if (!mountedRef.current) return null;
        const data = res?.data;
        if (!data) return;
        setEmployeeData(data);
        setIsLoading(false);
        setValuesForEdit(data);
      })
      .catch((error) => {
        if (!mountedRef.current) return null;
        setIsLoading(false);
        showErrorMessage(error);
      });
  };
  useEffect(() => {
    fetchReviewInfo();
  }, []);
  const displayStateTaxValues = () => {
    let innerHTML = "";
    for (const key in stateTaxes) {
      innerHTML += `<span> ${key} </span>`;
      innerHTML += `<p> ${stateTaxes[key]} </p>`;
    }
    return innerHTML;
  };
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const handleEditModalClose = () => {
    setShowPersonalInfoEdit(false);
    setShowAddressInfoEdit(false);
    setShowFederalTaxEdit(false);
    setShowStateTaxEdit(false);
    setShowPaymentMethodEdit(false);
  };

  const updateHomeAddress = (value: HomeAddress) => {
    setHomeAddress(value);
    handleEditModalClose();
  };
  const updateFederalTaxes = (value: FederalTaxInfo) => {
    setFederalTaxInfo(value);
    handleEditModalClose();
  };
  const updateStateTaxes = (value: any) => {
    formatStateTaxes(value);
    setStateTaxesUnformatted(value);
    handleEditModalClose();
  };
  const updatePersonalInfo = (value: PersonalInfo) => {
    setPersonalInfo(value);
    handleEditModalClose();
  };
  const updatePaymentMethod = (isValueChanged: boolean) => {
    if (isValueChanged) {
      handleEditModalClose();
      fetchReviewInfo();
      return;
    }
    handleEditModalClose();
  };
  return (
    <>
      <Formik onSubmit={onSubmit} innerRef={props.formRef} initialValues={{}}>
        {isLoading ? (
          <Loader />
        ) : (
          <div className="review-info user-details__content">
            <PersonalInfoEditModal
              open={showPersonalInfoEdit}
              handleClose={() => handleEditModalClose()}
              personalInfo={personalInfo}
              personalInfoUpdateCallback={updatePersonalInfo}
            />
            <AddressInfoEditModal
              open={showAddressInfoEdit}
              handleClose={() => handleEditModalClose()}
              homeAddress={homeAddress}
              AddressUpdateCallback={updateHomeAddress}
            />

            <FederalTaxInfoEditModal
              open={showFederalTaxEdit}
              handleClose={() => handleEditModalClose()}
              federalTaxInfo={federalTaxInfo}
              FederalTaxUpdateCallback={updateFederalTaxes}
            />
            <StateTaxInfoEditModal
              open={showStateTaxEdit}
              handleClose={() => handleEditModalClose()}
              stateTaxInfo={stateTaxesUnformatted}
              StateTaxUpdateCallback={updateStateTaxes}
              usStates={usStates}
            />
            <PaymentMethodEditModal
              open={showPaymentMethodEdit}
              handleClose={() => handleEditModalClose()}
              paymentDetails={paymentDetails}
              PaymentMethodUpdateCallback={updatePaymentMethod}
              homeAddress={homeAddress}
              bankAccount={bankAccount}
            />
            {isScrollable && !isShowFooterLoader && (
              <div
                className="scroll-arrow"
                onClick={() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: "smooth",
                  });
                }}
              >
                <img src={DownwardArrow} alt="Scroll Down" />
              </div>
            )}
            <div className="user-details--section">
              <div className="review-info__personal-info">
                <div className="info-header">
                  <Typography className="" variant="h6">
                    Personal Information
                  </Typography>
                  <a
                    className="primary-link"
                    onClick={() => {
                      setShowPersonalInfoEdit(true);
                    }}
                  >
                    <span>
                      <img src={EditIcon} alt="Edit Info" />
                    </span>
                    Edit
                  </a>
                </div>
                <div className="info-content">
                  <span>Name</span>
                  <p>
                    {personalInfo?.first_name} {personalInfo?.middle_initial}{" "}
                    {personalInfo?.last_name}
                  </p>

                  <span>Email</span>
                  <p>{personalInfo?.email}</p>

                  <span>Date of Birth</span>
                  <p> {personalInfo?.date_of_birth}</p>

                  <span>Social Security Number</span>
                  <p> {"XXX-XX-XXXX"}</p>
                </div>
              </div>
              <div className="review-info__home-address">
                <div className="info-header">
                  <Typography className="" variant="h6">
                    Home Address
                  </Typography>
                  <a
                    className="primary-link"
                    onClick={() => {
                      setShowAddressInfoEdit(true);
                    }}
                  >
                    <span>
                      <img src={EditIcon} alt="Edit Info" />
                    </span>
                    Edit
                  </a>
                </div>
                <div className="info-content">
                  <span>Home Address</span>
                  <p>
                    {homeAddress?.street_1}
                    <br />
                    {homeAddress?.street_2}
                    {homeAddress?.street_2 && <br />}
                    {homeAddress?.city}
                    <br />
                    {homeAddress?.state}
                    <br />
                    {homeAddress?.zip}
                  </p>
                </div>
              </div>

              <div className="review-info__federal-taxes">
                <div className="info-header">
                  <Typography className="" variant="h6">
                    Federal Taxes
                  </Typography>
                  <a
                    className="primary-link"
                    onClick={() => {
                      setShowFederalTaxEdit(true);
                    }}
                  >
                    <span>
                      <img src={EditIcon} alt="Edit Info" />
                    </span>
                    Edit
                  </a>
                </div>

                <div className="info-content">
                  <span>Filing Status</span>
                  <p>{federalTaxInfo?.filing_status}</p>

                  <span>Dependents Amount</span>
                  <p>{federalTaxInfo?.dependents_amount}</p>

                  <span>Other Income</span>
                  <p>{federalTaxInfo?.other_income}</p>

                  <span>Multiple Jobs?</span>
                  <p>{federalTaxInfo?.two_jobs ? "Yes" : "No"}</p>

                  <span>Extra Withholdings</span>
                  <p>{federalTaxInfo?.extra_withholding}</p>

                  <span>Deductions</span>
                  <p>{federalTaxInfo?.deductions}</p>
                </div>
              </div>

              <div className="review-info__state-taxes">
                <div className="info-header">
                  <Typography className="" variant="h6">
                    State Taxes
                  </Typography>
                  <a
                    className="primary-link"
                    onClick={() => {
                      setShowStateTaxEdit(true);
                    }}
                  >
                    <span>
                      <img src={EditIcon} alt="Edit Info" />
                    </span>
                    Edit
                  </a>
                </div>

                <div className="info-content">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: displayStateTaxValues(),
                    }}
                  />
                </div>
              </div>
              <div className="review-info__payment-method">
                <div className="info-header">
                  <Typography className="" variant="h6">
                    Payment Method
                  </Typography>
                  <a
                    className="primary-link"
                    onClick={() => {
                      setShowPaymentMethodEdit(true);
                    }}
                  >
                    <span>
                      <img src={EditIcon} alt="Edit Info" />
                    </span>
                    Edit
                  </a>
                </div>
                <div className="info-content">
                  <span>Payment Method</span>
                  <p>{paymentDetails?.type}</p>
                </div>
              </div>

              {paymentDetails?.type !== PAYMENT_TYPES.check && (
                <div className="review-info__bank-account">
                  <div className="info-header">
                    <Typography className="" variant="h6">
                      Bank Account
                    </Typography>
                  </div>
                  <div className="info-content">
                    <span>Bank Name</span>
                    <p>{bankAccount?.name}</p>

                    <span>Routing Number</span>
                    <p>{bankAccount?.routing_number}</p>

                    <span>Account Number</span>
                    <p>{bankAccount?.account_number}</p>

                    <span>Account Type</span>
                    <p>{bankAccount?.account_type}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Formik>
      {isShowFooterLoader && <Loader footerLoader={true} />}
    </>
  );
}
const mapStateToProps = (state) => ({
  usStates: state.config.employees?.states,
  isNavigateToNext: state.navigationFromMenu.isNavigateToNext,
});
export default connect(mapStateToProps)(ReviewInfo);
